/* eslint-disable react/require-default-props */
import {
  SelectHTMLAttributes,
  forwardRef,
  useRef,
  useEffect,
  useState,
  useCallback,
  ForwardedRef,
  useImperativeHandle,
  Dispatch,
  SetStateAction,
  CSSProperties,
} from 'react';

import { useField } from '@unform/core';
import { IoIosArrowDown } from 'react-icons/io';

import * as S from './styles';

export interface SelectOptionProps {
  label?: string;
  value?: string | number;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  options?: SelectOptionProps[];
  fieldLabel?: string;
  value?: string;
  setValue?: Dispatch<SetStateAction<string>>;
  containerStyle?: CSSProperties;
  initialValue?: SelectOptionProps;
  isDisabled?: boolean;
  className?: string;
  setParentFocus?: () => void;
}

const Select = forwardRef(
  (
    {
      name,
      options,
      setValue,
      setParentFocus,
      containerStyle = {},
      fieldLabel,
      initialValue,
      isDisabled = false,
      className,
      ...props
    }: SelectProps,
    innerRef?: ForwardedRef<HTMLSelectElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const { fieldName, defaultValue, registerField, error } = useField(name);
    const inputRef = useRef<HTMLSelectElement>(null);

    useImperativeHandle<HTMLSelectElement | null, HTMLSelectElement | null>(
      innerRef,
      () => inputRef.current,
      [],
    );

    const handleInputFocus = useCallback((): void => {
      setIsFocused(true);
      if (setParentFocus) {
        setParentFocus();
      }
    }, [setParentFocus]);

    const handleInputBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    const handleChange = useCallback(
      (e) => {
        setValue && setValue(e.target.value);
      },
      [setValue],
    );

    useEffect(() => {
      registerField<string>({
        name: fieldName,
        ref: inputRef.current,
        path: 'value',
      });
    }, [fieldName, registerField]);

    return (
      <S.SelectWrapper className={className}>
        {fieldLabel && <S.Label htmlFor={name}>{fieldLabel}</S.Label>}
        <S.SelectBlock
          isErrored={!!error}
          isFocused={isFocused}
          isDisabled={isDisabled}
          style={containerStyle}
        >
          <S.SelectField
            name={name}
            defaultValue={defaultValue}
            ref={inputRef}
            onChange={handleChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            isFocused={isFocused}
            disabled={isDisabled}
            {...props}
          >
            <option value={initialValue?.value || ''}>
              {initialValue?.label || 'Selecione uma opção'}
            </option>
            {options
              ?.filter((option) => option.value !== initialValue?.value)
              .map((option) => (
                <option key={option.value || ''} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
          </S.SelectField>
          <IoIosArrowDown
            size={20}
            style={{ position: 'absolute', right: 20, zIndex: -1 }}
          />
        </S.SelectBlock>
      </S.SelectWrapper>
    );
  },
);

export default Select;
